import { api, unwrap, type APIResponse, type Paginated } from "./client";

// Mirrors app/routers/extension.py ExtensionWithStatus (extends ExtensionOut).
export interface Extension {
  extension_id: number;
  name: string;
  title: string | null;
  sip_username: string | null;
  dialstring: string | null;
  forward_to: string | null;
  is_sip: boolean;
  is_iax: boolean;
  is_web: boolean;
  created_by: number | null;
  is_active: boolean;
  agent_status: string | null;
  is_logged: boolean;
  is_paused: boolean;
}

export interface ExtensionListParams {
  page?: number;
  per_page?: number;
  active_only?: boolean;
}

export function extensionType(e: Extension): string {
  if (e.is_sip) return "SIP";
  if (e.is_iax) return "IAX2";
  if (e.is_web) return "WebRTC";
  return "—";
}

// Mirrors app/services/pjsip_naming.py — the ONE rule for what Asterisk will
// accept as a PJSIP section name (the endpoint id, its aor, and the derived
// "<name>-auth" auth id). bin/provision_webrtc_endpoints.py silently drops
// any `is_web` extension whose name fails this, with nothing surfaced
// anywhere except a comment in a config file on the Asterisk box (beads A3)
// — this lets the form catch it at the point of entry instead of a save
// succeeding and the seat quietly never working.
//
// Scope: WebRTC ("is_web") extensions only. SIP/IAX2 extensions resolve to a
// Local-channel dialstring, not a PJSIP section, and prod already has
// long-lived working rows with spaces there (e.g. "MCA transfer") — never
// apply this to them.
const SAFE_PJSIP_NAME_RE = /^[A-Za-z0-9_-]+$/;

export function isSafeWebExtensionName(name: string): boolean {
  return SAFE_PJSIP_NAME_RE.test(name);
}

// Plain English, names what's allowed — matches the wording of the backend's
// 422 (app/services/pjsip_naming.describe_unsafe_name) so an operator sees
// the same explanation whether the form catches it locally or the API does.
export const UNSAFE_WEB_EXTENSION_NAME_MESSAGE =
  "WebRTC extension names can only use letters, numbers, hyphens, and underscores — no spaces or other punctuation.";

export async function listExtensions(params: ExtensionListParams): Promise<Paginated<Extension>> {
  // Trailing slash: route is at "/" (avoids 307 redirect).
  const resp = await api.get<Paginated<Extension>>("/extensions/", { params });
  if (!resp.data.success) throw new Error(resp.data.error || "Failed to load extensions");
  return resp.data;
}

// Mirrors app/routers/extension.py ExtensionCreate.
export interface ExtensionInput {
  name: string;
  secret: string;
  title?: string | null;
  sip_username?: string | null;
  dialstring?: string | null;
  forward_to?: string | null;
  is_sip: boolean;
  is_iax: boolean;
  is_web: boolean;
}

export async function createExtension(body: ExtensionInput): Promise<number> {
  const resp = await api.post<APIResponse<{ extension_id: number }>>("/extensions/", body);
  return unwrap(resp.data).extension_id;
}

// ExtensionUpdate — all fields optional.
export async function updateExtension(id: number, body: Partial<ExtensionInput> & { is_active?: boolean }): Promise<void> {
  await api.put(`/extensions/${id}`, body);
}

export async function deleteExtension(id: number): Promise<void> {
  await api.delete(`/extensions/${id}`);
}

// ---------------------------------------------------------------------------
// Self-service agent seats — /extensions/agents
// ---------------------------------------------------------------------------
// Mirrors app/routers/extension.py's AgentSeat* models. Note the absence of a
// `secret` field: the backend never returns one here by design. Agents get
// their SIP credentials from GET /agent/sip-credentials (owner-scoped,
// no-store), which is what AgentConsole already uses.

export interface AgentSeat {
  extension_id: number;
  name: string;
  title: string | null;
  is_active: boolean;
  created_at: string;
  agent_status: string | null;
  is_logged: boolean;
  is_paused: boolean;
  in_use: boolean;
}

export interface AgentSeatList {
  seats: AgentSeat[];
  used: number;
  cap: number;
}

export interface AgentSeatCreated {
  extension_id: number;
  name: string;
  title: string;
}

// No trailing slash: the backend route is "/agents" exactly (a trailing slash
// would 307 and can drop the auth header through the proxy).
export async function listAgentSeats(): Promise<AgentSeatList> {
  const resp = await api.get<APIResponse<AgentSeatList>>("/extensions/agents");
  return unwrap(resp.data);
}

// `name` is the operator's own extension number (prod runs 1001–1017 by
// convention); omitted, the backend allocates the next free one at or above
// its base. Either way the backend validates it against every PJSIP object id
// on the box — a duplicate would take every agent endpoint down (beads-50x).
export async function createAgentSeat(title?: string, name?: string): Promise<AgentSeatCreated> {
  const resp = await api.post<APIResponse<AgentSeatCreated>>("/extensions/agents", {
    title: title?.trim() ? title.trim() : null,
    name: name?.trim() ? name.trim() : null,
  });
  return unwrap(resp.data);
}

// Mirrors AgentSeatAsterisk. What the PBX itself says about the caller's
// seats: `provisioned` = Asterisk has the endpoint loaded, `registered` = a
// softphone is currently signed into it. When `reachable` is false the
// question could not be asked and both lists mean nothing — render "unknown",
// never "missing".
export interface AgentSeatAsterisk {
  checked_at: string;
  reachable: boolean;
  provisioned: string[];
  registered: string[];
}

export async function getAgentSeatsAsterisk(): Promise<AgentSeatAsterisk> {
  const resp = await api.get<APIResponse<AgentSeatAsterisk>>("/extensions/agents/asterisk");
  return unwrap(resp.data);
}

export type SeatAsteriskState = "registered" | "provisioned" | "pending" | "missing" | "unknown";

// How many provisioner cycles (~2 min each) to allow before an absent endpoint
// is a fault rather than a wait.
export const PROVISION_GRACE_MS = 5 * 60 * 1000;

/** One word for the seat's standing with Asterisk, derived in one place so the
 * row badge and the summary line can never disagree. */
export function seatAsteriskState(
  seat: AgentSeat,
  view: AgentSeatAsterisk | undefined,
  now: number = Date.now(),
): SeatAsteriskState {
  if (!view || !view.reachable) return "unknown";
  if (view.registered.includes(seat.name)) return "registered";
  if (view.provisioned.includes(seat.name)) return "provisioned";
  const age = now - new Date(seat.created_at).getTime();
  return age < PROVISION_GRACE_MS ? "pending" : "missing";
}

export async function deleteAgentSeat(id: number): Promise<void> {
  await api.delete(`/extensions/agents/${id}`);
}

/** Readable message for a failed seat mutation.
 *
 * The cap rejection is a FastAPI 409 `{detail: "..."}` rather than the
 * `{success, error}` envelope `unwrap` understands, and its text is the whole
 * point of the error ("Agent seat limit reached (25)"). Without this the page
 * would show axios's "Request failed with status code 409".
 */
export function agentSeatErrorMessage(err: unknown): string {
  const ax = err as {
    response?: { data?: { detail?: string; error?: string } };
    message?: string;
  };
  return (
    ax?.response?.data?.detail ||
    ax?.response?.data?.error ||
    ax?.message ||
    "Something went wrong. Please try again."
  );
}

/** Human label for a seat's live presence, or null when it is simply offline. */
export function agentSeatStatus(seat: AgentSeat): "on a call" | "on break" | "available" | null {
  if (!seat.is_logged) return null;
  if (seat.in_use) return "on a call";
  if (seat.is_paused) return "on break";
  return "available";
}
